import { motion } from 'framer-motion';
import { useState, useRef } from 'react';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';
import emailjs from '@emailjs/browser';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

function Contact() {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    investmentAmount: '',
    preferredPlan: 'standard'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        'template_yk9acue',  // Using the new template ID
        {
          name: formData.name,        // Changed from from_name to name
          email: formData.email,      // Changed from from_email to email
          phone: formData.phone,
          investment_amount: formData.investmentAmount,
          preferred_plan: formData.preferredPlan,
          message: formData.message
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your inquiry! We will contact you within 2 hours.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        investmentAmount: '',
        preferredPlan: 'standard'
      });
    } catch (error) {
      console.error('Email send failed:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: "Investment Hotline",
      details: ["+1 (424) 292-0631"],
      description: "24/7 Priority Support for Investors"
    },
    {
      icon: EnvelopeIcon,
      title: "Email Support",
      details: ["support@quickwealthinvestment.com"],
      description: "Response within 2 hours for investment inquiries"
    },
    {
      icon: MapPinIcon,
      title: "Investment Office",
      details: ["6930 Beach Blvd | 141, Buena Park. CA 90621"],
      description: "Schedule a private consultation"
    },
    {
      icon: ClockIcon,
      title: "Trading Hours",
      details: ["24/7 Online Platform Access", "Office Hours: Mon-Fri 9AM-6PM EST"],
      description: "Investment processing available 24/7"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Hero Section */}
      <motion.section 
        className="relative pt-24 pb-16 bg-primary-800 text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900 to-primary-800 opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              className="text-4xl sm:text-5xl font-bold"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Start Your Investment Journey
            </motion.h1>
            <motion.p 
              className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Ready to earn up to 20x returns in 21 days? Our investment experts are here to guide you through our high-yield investment plans and help you maximize your wealth potential.
            </motion.p>
          </div>
        </div>
      </motion.section>

      {/* Contact Form Section */}
      <motion.section 
        className="py-16 bg-gray-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white">Investment Inquiry</h2>
              <p className="mt-4 text-gray-300">Fill out the form below and our investment advisors will contact you within 2 hours.</p>
            </div>
            <motion.form 
              ref={formRef}
              onSubmit={handleSubmit}
              className="space-y-6 bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl shadow-xl border border-gray-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              {submitStatus.message && (
                <div className={`p-4 rounded-md ${
                  submitStatus.type === 'success' ? 'bg-green-800/50 text-green-100' : 'bg-red-800/50 text-red-100'
                }`}>
                  {submitStatus.message}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-200">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 placeholder-gray-400"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200">Email</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 placeholder-gray-400"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-200">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 placeholder-gray-400"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="investmentAmount" className="block text-sm font-medium text-gray-200">Investment Amount ($)</label>
                  <input
                    type="number"
                    name="investmentAmount"
                    id="investmentAmount"
                    value={formData.investmentAmount}
                    onChange={handleChange}
                    min="1000"
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 placeholder-gray-400"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="preferredPlan" className="block text-sm font-medium text-gray-200">Preferred Investment Plan</label>
                <select
                  name="preferredPlan"
                  id="preferredPlan"
                  value={formData.preferredPlan}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  required
                >
                  <option value="standard">Standard Plan ($1,000 - $4,000)</option>
                  <option value="premium">Premium Plan ($5,000 - $9,000)</option>
                  <option value="advanced">Advanced Plan ($10,000 upwards)</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-200">Additional Information</label>
                <textarea
                  name="message"
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Any specific questions about our investment plans?"
                  className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 placeholder-gray-400"
                />
              </div>
              <div className="text-center">
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex items-center px-8 py-3 border border-transparent text-lg font-medium rounded-md text-white 
                    ${isSubmitting ? 'bg-primary-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-primary-500 transition-colors duration-200`}
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                >
                  {isSubmitting ? 'Sending...' : 'Submit Investment Inquiry'}
                </motion.button>
              </div>
            </motion.form>
          </div>
        </div>
      </motion.section>

      {/* Contact Info Cards */}
      <motion.section 
        className="py-16 bg-gray-800"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white">Get in Touch</h2>
            <p className="mt-4 text-gray-300">Multiple ways to reach us</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-lg shadow-xl p-8 border border-gray-700"
                whileHover={{ y: -5, backgroundColor: "rgba(17, 24, 39, 0.7)" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * index, duration: 0.5 }}
              >
                <div className="flex flex-col items-center text-center">
                  <item.icon className="h-8 w-8 text-primary-500 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-4">{item.title}</h3>
                  {item.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-300 mb-2">{detail}</p>
                  ))}
                  <p className="text-sm text-primary-400 mt-2">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Map Section */}
      <motion.section 
        className="py-16 bg-gray-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-xl overflow-hidden border border-gray-700">
            <iframe
              title="Office Location"
              className="w-full h-[400px]"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3315.0157803774747!2d-117.99060792357897!3d33.8381444288001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dd2a4c4d2df8b9%3A0x7edf0a3a7dc4520c!2s6930%20Beach%20Blvd%20%23141%2C%20Buena%20Park%2C%20CA%2090621%2C%20USA!5e0!3m2!1sen!2s!4v1689432234567!5m2!1sen!2s"
              loading="lazy"
              referrerPolicy="no-referrer-when-allowed"
              allowFullScreen
            />
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}

export default Contact;